import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  CheckCircle,
  AlertCircle,
  Loader2,
  MessageSquare,
  Bell,
  CheckSquare,
  Users,
  FolderPlus,
  Send,
  Save,
  RefreshCw,
  Building2,
  X,
} from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { pluginsApi, organizationsApi, PluginEventMapping } from '@/lib/api';

// Slack logo SVG
const SlackLogo = () => (
  <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor">
    <path d="M5.042 15.165a2.528 2.528 0 0 1-2.52 2.523A2.528 2.528 0 0 1 0 15.165a2.527 2.527 0 0 1 2.522-2.52h2.52v2.52zM6.313 15.165a2.527 2.527 0 0 1 2.521-2.52 2.527 2.527 0 0 1 2.521 2.52v6.313A2.528 2.528 0 0 1 8.834 24a2.528 2.528 0 0 1-2.521-2.522v-6.313zM8.834 5.042a2.528 2.528 0 0 1-2.521-2.52A2.528 2.528 0 0 1 8.834 0a2.528 2.528 0 0 1 2.521 2.522v2.52H8.834zM8.834 6.313a2.528 2.528 0 0 1 2.521 2.521 2.528 2.528 0 0 1-2.521 2.521H2.522A2.528 2.528 0 0 1 0 8.834a2.528 2.528 0 0 1 2.522-2.521h6.312zM18.956 8.834a2.528 2.528 0 0 1 2.522-2.521A2.528 2.528 0 0 1 24 8.834a2.528 2.528 0 0 1-2.522 2.521h-2.522V8.834zM17.688 8.834a2.528 2.528 0 0 1-2.523 2.521 2.527 2.527 0 0 1-2.52-2.521V2.522A2.527 2.527 0 0 1 15.165 0a2.528 2.528 0 0 1 2.523 2.522v6.312zM15.165 18.956a2.528 2.528 0 0 1 2.523 2.522A2.528 2.528 0 0 1 15.165 24a2.527 2.527 0 0 1-2.52-2.522v-2.522h2.52zM15.165 17.688a2.527 2.527 0 0 1-2.52-2.523 2.526 2.526 0 0 1 2.52-2.52h6.313A2.527 2.527 0 0 1 24 15.165a2.528 2.528 0 0 1-2.522 2.523h-6.313z" />
  </svg>
);

// Event type configuration
const EVENT_TYPES = [
  {
    type: 'discussion.notify',
    label: 'New Discussions',
    description: 'When a new discussion is started',
    icon: MessageSquare,
    globalDescription: 'Brief notification with link',
    detailedDescription: 'Full discussion content with preview',
  },
  {
    type: 'reply.notify',
    label: 'Discussion Replies',
    description: 'When someone replies to a discussion',
    icon: MessageSquare,
    globalDescription: 'Brief notification with link',
    detailedDescription: 'Full reply content with context',
  },
  {
    type: 'message.mention',
    label: 'Mentions',
    description: 'When someone is @mentioned',
    icon: Bell,
    globalDescription: 'Brief notification with link',
    detailedDescription: 'Full mention context',
  },
  {
    type: 'task.created',
    label: 'New Tasks',
    description: 'When a task is created',
    icon: CheckSquare,
    globalDescription: 'Brief notification with link',
    detailedDescription: 'Task details with assignee and due date',
  },
  {
    type: 'task.assigned',
    label: 'Task Assignments',
    description: 'When a task is assigned to someone',
    icon: Users,
    globalDescription: 'Brief notification with link',
    detailedDescription: 'Full task details',
  },
  {
    type: 'task.completed',
    label: 'Task Completions',
    description: 'When a task is marked complete',
    icon: CheckCircle,
    globalDescription: 'Brief notification with link',
    detailedDescription: 'Completion notice',
  },
  {
    type: 'task.comment',
    label: 'Task Comments',
    description: 'When someone comments on a task',
    icon: MessageSquare,
    globalDescription: 'Brief notification with link',
    detailedDescription: 'Comment content with task context',
  },
  {
    type: 'project.created',
    label: 'New Projects',
    description: 'When a project is created',
    icon: FolderPlus,
    globalDescription: 'Brief notification with link',
    detailedDescription: 'Project details',
  },
  {
    type: 'project.member_added',
    label: 'Project Members',
    description: 'When someone is added to a project',
    icon: Users,
    globalDescription: 'Brief notification with link',
    detailedDescription: 'Member details',
  },
  {
    type: 'dm.created',
    label: 'Direct Messages',
    description: 'When a direct message is sent',
    icon: Send,
    globalDescription: 'Brief notification with link',
    detailedDescription: 'Message preview (private)',
  },
];

interface EventConfig {
  enabled: boolean;
  channelId: string;
  channelName: string;
}

export function SettingsSlackConfig() {
  const { installationId } = useParams<{ installationId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const [showSuccess, setShowSuccess] = useState(searchParams.get('connected') === 'true');

  // Local state for event configurations
  const [globalChannel, setGlobalChannel] = useState<{ id: string; name: string } | null>(null);
  const [eventConfigs, setEventConfigs] = useState<Record<string, EventConfig>>({});
  const [hasChanges, setHasChanges] = useState(false);

  // Organization override state: orgId -> { channelId, channelName }
  const [orgOverrides, setOrgOverrides] = useState<Record<string, { channelId: string; channelName: string }>>({});
  const [hasOrgChanges, setHasOrgChanges] = useState(false);

  // Get installation details
  const { data: installation, isLoading: installationLoading } = useQuery({
    queryKey: ['slack-installation', installationId],
    queryFn: () => pluginsApi.getInstallation('slack', installationId!),
    enabled: !!installationId,
  });

  // Get available channels
  const { data: channelsData, isLoading: channelsLoading, refetch: refetchChannels } = useQuery({
    queryKey: ['slack-channels', installationId],
    queryFn: () => pluginsApi.slack.listChannels(installationId!),
    enabled: !!installationId && !!installation?.credentials?.hasAccessToken,
  });

  const channels = channelsData?.channels || [];

  const isConnected = installation?.credentials?.hasAccessToken;

  // Get client organizations
  const { data: organizations } = useQuery({
    queryKey: ['organizations'],
    queryFn: () => organizationsApi.list(),
    enabled: !!installationId && !!isConnected,
  });

  // Get existing org-scope mappings
  const { data: orgMappings } = useQuery({
    queryKey: ['slack-org-mappings', installationId],
    queryFn: () => pluginsApi.getEventMappings('slack', installationId!, { scope: 'organization' }),
    enabled: !!installationId && !!isConnected,
  });

  // Initialize org overrides from fetched mappings
  useEffect(() => {
    if (orgMappings) {
      const overrides: Record<string, { channelId: string; channelName: string }> = {};
      for (const mapping of orgMappings) {
        if (mapping.scope === 'organization' && mapping.scopeId) {
          const config = mapping.channelConfig as { channelId?: string; channelName?: string };
          if (config.channelId && !overrides[mapping.scopeId]) {
            overrides[mapping.scopeId] = {
              channelId: config.channelId,
              channelName: config.channelName || '',
            };
          }
        }
      }
      setOrgOverrides(overrides);
    }
  }, [orgMappings]);

  // Initialize configs from installation data (default scope only)
  useEffect(() => {
    if (installation?.eventMappings) {
      const configs: Record<string, EventConfig> = {};
      let globalCh: { id: string; name: string } | null = null;

      for (const mapping of installation.eventMappings) {
        // Only use default scope (or unmigrated) mappings for the main config
        const isDefaultScope = !mapping.scope || mapping.scope === 'default';

        if (mapping.isGlobal && !globalCh) {
          const config = mapping.channelConfig as { channelId?: string; channelName?: string };
          if (config.channelId) {
            globalCh = { id: config.channelId, name: config.channelName || '' };
          }
        }

        if (!mapping.isGlobal && isDefaultScope) {
          const config = mapping.channelConfig as { channelId?: string; channelName?: string };
          configs[mapping.eventType] = {
            enabled: mapping.enabled,
            channelId: config.channelId || '',
            channelName: config.channelName || '',
          };
        }
      }

      setGlobalChannel(globalCh);
      setEventConfigs(configs);
    }
  }, [installation]);

  // Auto-hide success message
  useEffect(() => {
    if (showSuccess) {
      const timer = setTimeout(() => setShowSuccess(false), 5000);
      return () => clearTimeout(timer);
    }
  }, [showSuccess]);

  // Save event mappings
  const saveMutation = useMutation({
    mutationFn: async () => {
      const mappings: Array<{
        eventType: string;
        enabled: boolean;
        channelConfig: { channelId: string; channelName: string };
        isGlobal: boolean;
      }> = [];

      // Add global channel mapping for all events if set
      if (globalChannel?.id) {
        for (const event of EVENT_TYPES) {
          mappings.push({
            eventType: event.type,
            enabled: true,
            channelConfig: {
              channelId: globalChannel.id,
              channelName: globalChannel.name,
            },
            isGlobal: true,
          });
        }
      }

      // Add per-event mappings
      for (const [eventType, config] of Object.entries(eventConfigs)) {
        if (config.enabled && config.channelId) {
          mappings.push({
            eventType,
            enabled: config.enabled,
            channelConfig: {
              channelId: config.channelId,
              channelName: config.channelName,
            },
            isGlobal: false,
          });
        }
      }

      return pluginsApi.bulkUpsertEventMappings('slack', installationId!, mappings);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['slack-installation', installationId] });
      setHasChanges(false);
    },
  });

  // Test connection
  const testMutation = useMutation({
    mutationFn: () => pluginsApi.testConnection('slack', installationId!),
  });

  const handleGlobalChannelChange = (channelId: string) => {
    const channel = channels.find((c) => c.id === channelId);
    setGlobalChannel(channel ? { id: channel.id, name: channel.name } : null);
    setHasChanges(true);
  };

  const handleEventToggle = (eventType: string, enabled: boolean) => {
    setEventConfigs((prev) => ({
      ...prev,
      [eventType]: {
        ...prev[eventType],
        enabled,
        channelId: prev[eventType]?.channelId || '',
        channelName: prev[eventType]?.channelName || '',
      },
    }));
    setHasChanges(true);
  };

  const handleEventChannelChange = (eventType: string, channelId: string) => {
    const channel = channels.find((c) => c.id === channelId);
    setEventConfigs((prev) => ({
      ...prev,
      [eventType]: {
        ...prev[eventType],
        enabled: prev[eventType]?.enabled ?? false,
        channelId,
        channelName: channel?.name || '',
      },
    }));
    setHasChanges(true);
  };

  const handleOrgOverrideChange = (orgId: string, channelId: string) => {
    const channel = channels.find((c) => c.id === channelId);
    setOrgOverrides((prev) => {
      if (!channelId) {
        const next = { ...prev };
        delete next[orgId];
        return next;
      }
      return {
        ...prev,
        [orgId]: { channelId, channelName: channel?.name || '' },
      };
    });
    setHasOrgChanges(true);
  };

  // Save org override mutations
  const saveOrgOverridesMutation = useMutation({
    mutationFn: async () => {
      // Get the current org mappings to determine what to create/update/delete
      const currentMappings = orgMappings || [];
      const currentOrgIds = new Set(
        currentMappings
          .filter((m) => m.scope === 'organization')
          .map((m) => m.scopeId)
      );

      // Determine which orgs to delete (had override before, now removed)
      const newOrgIds = new Set(Object.keys(orgOverrides));
      const toDelete = [...currentOrgIds].filter((id) => !newOrgIds.has(id));

      // Delete removed overrides
      for (const orgId of toDelete) {
        await pluginsApi.deleteEventMappingsByScope('slack', installationId!, 'organization', orgId);
      }

      // Upsert current overrides
      for (const [orgId, override] of Object.entries(orgOverrides)) {
        const mappings = EVENT_TYPES.map((event) => ({
          eventType: event.type,
          enabled: true,
          channelConfig: { channelId: override.channelId, channelName: override.channelName },
          isGlobal: false,
          scope: 'organization' as const,
          scopeId: orgId,
        }));
        await pluginsApi.bulkUpsertEventMappings('slack', installationId!, mappings);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['slack-org-mappings', installationId] });
      queryClient.invalidateQueries({ queryKey: ['slack-installation', installationId] });
      setHasOrgChanges(false);
    },
  });

  if (installationLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 text-grey-400 animate-spin" />
      </div>
    );
  }

  if (!installation) {
    return (
      <div className="p-8 text-center">
        <AlertCircle className="w-12 h-12 text-grey-400 mx-auto mb-4" />
        <h2 className="text-heading-2 text-secondary mb-2">Installation Not Found</h2>
        <p className="text-body text-grey-500 mb-4">
          This Slack installation could not be found.
        </p>
        <Button onClick={() => navigate('/settings/integrations')}>
          <ArrowLeft className="w-4 h-4" />
          Back to Integrations
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-3xl">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate('/settings/integrations')}
          className="p-2 text-grey-400 hover:text-secondary hover:bg-grey-100 rounded-lg transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-[#4A154B]/10 flex items-center justify-center text-[#4A154B]">
            <SlackLogo />
          </div>
          <div>
            <h2 className="text-heading-3 text-secondary">Slack Configuration</h2>
            <p className="text-sm text-grey-500 mt-1">
              Configure which Hub events are sent to Slack
            </p>
          </div>
        </div>
      </div>

      {/* Success banner */}
      {showSuccess && (
        <div className="p-4 bg-success/10 border border-success/20 rounded-lg flex items-center gap-3">
          <CheckCircle className="w-5 h-5 text-success shrink-0" />
          <p className="text-sm text-secondary">
            Successfully connected to Slack workspace <strong>{installation.credentials?.teamName}</strong>
          </p>
        </div>
      )}

      {/* Connection Status */}
      <Card className="p-6">
        <h3 className="text-heading-3 text-secondary mb-4">Connection Status</h3>

        {isConnected ? (
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-grey-50 rounded-lg">
              <div>
                <p className="text-sm font-medium text-secondary">
                  {installation.credentials?.teamName || 'Slack Workspace'}
                </p>
                {installation.credentials?.scope && (
                  <p className="text-xs text-grey-500 mt-1">
                    Scopes: {installation.credentials.scope}
                  </p>
                )}
              </div>
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-success/10 text-success text-xs font-medium rounded-full">
                <CheckCircle className="w-3 h-3" />
                Connected
              </span>
            </div>

            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => testMutation.mutate()}
                disabled={testMutation.isPending}
              >
                {testMutation.isPending ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <RefreshCw className="w-4 h-4" />
                )}
                Test Connection
              </Button>
              {testMutation.isSuccess && (
                <span className="text-sm text-success">
                  {testMutation.data?.success ? 'Connection successful' : testMutation.data?.message}
                </span>
              )}
            </div>
          </div>
        ) : (
          <div className="p-4 bg-warning/10 border border-warning/20 rounded-lg">
            <p className="text-sm text-secondary">
              Slack is not connected. Please reconnect from the Integrations page.
            </p>
          </div>
        )}
      </Card>

      {/* Global Channel */}
      {isConnected && (
        <Card className="p-6">
          <div className="mb-4">
            <h3 className="text-heading-3 text-secondary mb-1">Activity Feed Channel</h3>
            <p className="text-sm text-grey-500 mt-1">
              Send a summary of all Hub activity to a single channel (like #hub)
            </p>
          </div>

          {channelsLoading ? (
            <div className="flex items-center gap-2 text-grey-500">
              <Loader2 className="w-4 h-4 animate-spin" />
              Loading channels...
            </div>
          ) : (
            <div className="space-y-3">
              <select
                value={globalChannel?.id || ''}
                onChange={(e) => handleGlobalChannelChange(e.target.value)}
                className="w-full px-3 py-2 bg-white border border-grey-200 rounded-lg text-sm text-secondary focus:ring-2 focus:ring-accent/50 focus:border-accent"
              >
                <option value="">No activity feed channel</option>
                {channels.map((channel) => (
                  <option key={channel.id} value={channel.id}>
                    {channel.is_private ? '🔒' : '#'} {channel.name}
                  </option>
                ))}
              </select>
              {globalChannel && (
                <p className="text-xs text-grey-500">
                  All Hub events will send brief notifications to #{globalChannel.name}
                </p>
              )}
            </div>
          )}

          <button
            onClick={() => refetchChannels()}
            className="mt-3 text-xs text-accent hover:underline flex items-center gap-1"
          >
            <RefreshCw className="w-3 h-3" />
            Refresh channel list
          </button>
        </Card>
      )}

      {/* Per-Event Configuration */}
      {isConnected && (
        <Card className="p-6">
          <div className="mb-6">
            <h3 className="text-heading-3 text-secondary mb-1">Event Notifications</h3>
            <p className="text-sm text-grey-500 mt-1">
              Configure detailed notifications for specific events (in addition to the activity feed)
            </p>
          </div>

          <div className="space-y-4">
            {EVENT_TYPES.map((event) => {
              const config = eventConfigs[event.type] || { enabled: false, channelId: '', channelName: '' };
              const Icon = event.icon;

              return (
                <div
                  key={event.type}
                  className={`p-4 rounded-lg border transition-colors ${
                    config.enabled
                      ? 'border-accent/30 bg-accent/5'
                      : 'border-grey-200 bg-grey-50'
                  }`}
                >
                  <div className="flex items-start gap-4">
                    <div
                      className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
                        config.enabled ? 'bg-accent/10 text-accent' : 'bg-grey-100 text-grey-400'
                      }`}
                    >
                      <Icon className="w-4 h-4" />
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-1">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={config.enabled}
                            onChange={(e) => handleEventToggle(event.type, e.target.checked)}
                            className="w-4 h-4 rounded border-grey-300 text-accent focus:ring-accent"
                          />
                          <span className="font-medium text-secondary">{event.label}</span>
                        </label>
                      </div>
                      <p className="text-xs text-grey-500 mb-3">{event.description}</p>

                      {config.enabled && (
                        <div className="mt-3">
                          <label className="text-xs font-medium text-secondary mb-1 block">
                            Send detailed notifications to:
                          </label>
                          <select
                            value={config.channelId}
                            onChange={(e) => handleEventChannelChange(event.type, e.target.value)}
                            className="w-full px-3 py-2 bg-white border border-grey-200 rounded-lg text-sm text-secondary focus:ring-2 focus:ring-accent/50 focus:border-accent"
                          >
                            <option value="">Select a channel</option>
                            {channels.map((channel) => (
                              <option key={channel.id} value={channel.id}>
                                {channel.is_private ? '🔒' : '#'} {channel.name}
                              </option>
                            ))}
                          </select>
                          {config.channelId && (
                            <p className="text-xs text-grey-500 mt-1">
                              {event.detailedDescription}
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {/* Organization Channel Overrides */}
      {isConnected && organizations && organizations.length > 0 && (
        <Card className="p-6">
          <div className="mb-6">
            <h3 className="text-heading-3 text-secondary mb-1">Organization Channel Overrides</h3>
            <p className="text-sm text-grey-500 mt-1">
              Send notifications for specific organizations to their own Slack channel instead of the default
            </p>
          </div>

          <div className="space-y-3">
            {organizations.map((org) => {
              const override = orgOverrides[org.id];

              return (
                <div
                  key={org.id}
                  className={`p-4 rounded-lg border transition-colors ${
                    override
                      ? 'border-accent/30 bg-accent/5'
                      : 'border-grey-200 bg-grey-50'
                  }`}
                >
                  <div className="flex items-center gap-4">
                    <div
                      className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
                        override ? 'bg-accent/10 text-accent' : 'bg-grey-100 text-grey-400'
                      }`}
                    >
                      <Building2 className="w-4 h-4" />
                    </div>

                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-secondary text-sm">{org.name}</p>
                    </div>

                    <div className="flex items-center gap-2 shrink-0">
                      <select
                        value={override?.channelId || ''}
                        onChange={(e) => handleOrgOverrideChange(org.id, e.target.value)}
                        className="px-3 py-1.5 bg-white border border-grey-200 rounded-lg text-sm text-secondary focus:ring-2 focus:ring-accent/50 focus:border-accent min-w-[180px]"
                      >
                        <option value="">Use default</option>
                        {channels.map((channel) => (
                          <option key={channel.id} value={channel.id}>
                            {channel.is_private ? '🔒' : '#'} {channel.name}
                          </option>
                        ))}
                      </select>
                      {override && (
                        <button
                          onClick={() => handleOrgOverrideChange(org.id, '')}
                          className="p-1 text-grey-400 hover:text-danger transition-colors"
                          title="Remove override"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {hasOrgChanges && (
            <div className="mt-4 flex justify-end">
              <Button
                onClick={() => saveOrgOverridesMutation.mutate()}
                disabled={saveOrgOverridesMutation.isPending}
                size="sm"
              >
                {saveOrgOverridesMutation.isPending ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Save className="w-4 h-4" />
                )}
                Save Organization Overrides
              </Button>
            </div>
          )}

          {saveOrgOverridesMutation.isError && (
            <p className="mt-2 text-sm text-danger">
              Failed to save organization overrides. Please try again.
            </p>
          )}
        </Card>
      )}

      {/* Save Button */}
      {isConnected && hasChanges && (
        <div className="sticky bottom-4 flex justify-end">
          <Button
            onClick={() => saveMutation.mutate()}
            disabled={saveMutation.isPending}
            className="shadow-lg"
          >
            {saveMutation.isPending ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Save className="w-4 h-4" />
            )}
            Save Configuration
          </Button>
        </div>
      )}

      {/* Save success feedback */}
      {saveMutation.isSuccess && !hasChanges && (
        <div className="fixed bottom-4 right-4 p-4 bg-success text-white rounded-lg shadow-lg flex items-center gap-2">
          <CheckCircle className="w-5 h-5" />
          Configuration saved
        </div>
      )}
    </div>
  );
}
